import { Check, X, Quote } from "lucide-react";
import { Reveal } from "@/components/common/Reveal";
import { Icon } from "@/components/common/Icon";
import { AlertBox } from "@/components/common/AlertBox";
import { Timeline } from "./Timeline";
import { ComparisonTable } from "./ComparisonTable";
import { FAQAccordion } from "./FAQAccordion";

const Heading = ({ children }) =>
  children ? (
    <h2 className="font-serif text-2xl md:text-3xl font-medium text-charcoal mb-6">
      {children}
    </h2>
  ) : null;

const Block = ({ block }) => {
  switch (block.type) {
    case "prose":
      return (
        <div>
          <Heading>{block.heading}</Heading>
          <div className="space-y-4">
            {block.paragraphs.map((p, i) => (
              <p key={i} className="text-base md:text-lg leading-relaxed font-light text-slatey">
                {p}
              </p>
            ))}
          </div>
        </div>
      );

    case "timeline":
      return (
        <div>
          <Heading>{block.heading}</Heading>
          <Timeline items={block.items} />
        </div>
      );

    case "table":
      return (
        <div>
          <Heading>{block.heading}</Heading>
          <ComparisonTable columns={block.columns} rows={block.rows} />
        </div>
      );

    case "accordion":
      return (
        <div>
          <Heading>{block.heading}</Heading>
          <FAQAccordion items={block.items} />
        </div>
      );

    case "tips":
      return (
        <div>
          <Heading>{block.heading}</Heading>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {block.items.map((item, i) => (
              <div
                key={i}
                className="rounded-2xl border border-stone-line bg-white p-6 transition-all hover:-translate-y-1 hover:shadow-soft"
              >
                <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-gold/10 text-gold-dark">
                  <Icon name={item.icon} size={20} />
                </div>
                <h4 className="font-serif text-xl font-medium text-charcoal">{item.title}</h4>
                <p className="mt-1.5 text-[15px] leading-relaxed font-light text-slatey">
                  {item.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      );

    case "checklist": {
      const isDont = block.tone === "dont";
      return (
        <div>
          <Heading>{block.heading}</Heading>
          <ul className="grid gap-3 sm:grid-cols-2">
            {block.items.map((item, i) => (
              <li
                key={i}
                className="flex items-start gap-3 rounded-xl border border-stone-line bg-white px-5 py-4"
              >
                <span
                  className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${
                    isDont ? "bg-roman-red/10 text-roman-red" : "bg-gold/15 text-gold-dark"
                  }`}
                >
                  {isDont ? <X size={13} strokeWidth={2.5} /> : <Check size={13} strokeWidth={2.5} />}
                </span>
                <span className="text-[15px] font-light text-charcoal">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      );
    }

    case "twocol":
      return (
        <div>
          <Heading>{block.heading}</Heading>
          <div className="grid gap-6 md:grid-cols-2">
            {[block.left, block.right].map((col, ci) => (
              <div
                key={ci}
                className={`rounded-2xl border p-6 md:p-7 ${
                  ci === 0
                    ? "border-gold/30 bg-gold/[0.05]"
                    : "border-roman-red/20 bg-roman-red/[0.04]"
                }`}
              >
                <h4 className="font-serif text-xl font-medium text-charcoal">{col.title}</h4>
                <ul className="mt-4 space-y-2.5">
                  {col.items.map((it, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-[15px] text-slatey">
                      <span
                        className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${
                          ci === 0 ? "bg-gold/15 text-gold-dark" : "bg-roman-red/10 text-roman-red"
                        }`}
                      >
                        {ci === 0 ? <Check size={13} strokeWidth={2.5} /> : <X size={13} strokeWidth={2.5} />}
                      </span>
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      );

    case "steps":
      return (
        <div>
          <Heading>{block.heading}</Heading>
          <ol className="space-y-4">
            {block.items.map((item, i) => (
              <li
                key={i}
                className="flex gap-5 rounded-2xl border border-stone-line bg-white p-6"
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-charcoal font-serif text-lg font-medium text-white">
                  {i + 1}
                </span>
                <div>
                  <h4 className="font-serif text-xl font-medium text-charcoal">{item.title}</h4>
                  <p className="mt-1 text-[15px] leading-relaxed font-light text-slatey">
                    {item.text}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      );

    case "stats":
      return (
        <div className="grid grid-cols-3 gap-4 rounded-2xl border border-stone-line bg-white p-8">
          {block.items.map((s, i) => (
            <div key={i} className="text-center">
              <div className="font-serif text-3xl md:text-5xl font-medium text-gold-dark">
                {s.value}
              </div>
              <div className="mt-1 text-xs md:text-sm uppercase tracking-wider text-slatey">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      );

    case "quote":
      return (
        <blockquote className="relative rounded-2xl bg-charcoal p-8 md:p-12 text-white">
          <Quote size={36} className="text-gold/50" />
          <p className="mt-4 font-serif text-2xl md:text-3xl font-light leading-snug text-balance">
            {block.text}
          </p>
          {block.author && (
            <footer className="mt-5 text-sm uppercase tracking-[0.18em] text-gold-light">
              — {block.author}
            </footer>
          )}
        </blockquote>
      );

    case "alert":
      return <AlertBox tone={block.tone} title={block.title} text={block.text} />;

    case "map":
      return (
        <div>
          <Heading>{block.heading}</Heading>
          <div className="overflow-hidden rounded-2xl border border-stone-line">
            <iframe
              title="Colosseum map"
              src={block.embed}
              className="h-[360px] w-full md:h-[440px]"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
          {block.caption && (
            <p className="mt-3 text-sm text-slatey">{block.caption}</p>
          )}
        </div>
      );

    case "image":
      return (
        <figure>
          <img
            src={block.src}
            alt={block.caption || "Colosseum"}
            loading="lazy"
            className="w-full rounded-2xl border border-stone-line object-cover"
          />
          {block.caption && (
            <figcaption className="mt-3 text-sm text-slatey">{block.caption}</figcaption>
          )}
        </figure>
      );

    default:
      return null;
  }
};

export const SectionRenderer = ({ blocks = [] }) => (
  <div className="space-y-14 md:space-y-20">
    {blocks.map((block, i) => (
      <Reveal key={i}>
        <Block block={block} />
      </Reveal>
    ))}
  </div>
);

export default SectionRenderer;
